import { Theme } from '../types';

export const defaultTheme: Theme = {
  name: 'Default Light',
  id: 'default-light',
  
  colors: {
    // Primary colors - Blue palette
    primary: '#3b82f6',
    primaryLight: '#60a5fa',
    primaryDark: '#2563eb',
    primaryAlpha10: 'rgba(59, 130, 246, 0.1)',
    primaryAlpha20: 'rgba(59, 130, 246, 0.2)',
    primaryAlpha30: 'rgba(59, 130, 246, 0.3)',
    
    // Secondary colors - Purple palette
    secondary: '#8b5cf6',
    secondaryLight: '#a78bfa',
    secondaryDark: '#7c3aed',
    secondaryAlpha10: 'rgba(139, 92, 246, 0.1)',
    secondaryAlpha20: 'rgba(139, 92, 246, 0.2)',
    
    // Accent colors - Indigo
    accent: '#6366f1',
    accentLight: '#818cf8',
    accentDark: '#4f46e5',
    
    // Semantic colors
    success: '#10b981',
    successLight: '#34d399',
    successDark: '#059669',
    warning: '#f59e0b',
    warningLight: '#fbbf24',
    warningDark: '#d97706',
    error: '#ef4444',
    errorLight: '#f87171',
    errorDark: '#dc2626',
    info: '#06b6d4',
    infoLight: '#22d3ee',
    infoDark: '#0891b2',
    
    // Neutral colors
    gray50: '#f9fafb',
    gray100: '#f3f4f6',
    gray200: '#e5e7eb',
    gray300: '#d1d5db',
    gray400: '#9ca3af',
    gray500: '#6b7280',
    gray600: '#4b5563',
    gray700: '#374151',
    gray800: '#1f2937',
    gray900: '#111827',
    
    // Background colors
    background: '#ffffff',
    backgroundSecondary: '#f9fafb',
    backgroundTertiary: '#f3f4f6',
    backgroundElevated: 'rgba(255, 255, 255, 0.95)',
    backgroundOverlay: 'rgba(0, 0, 0, 0.5)',
    
    // Surface colors
    surface: '#ffffff',
    surfaceHover: '#f9fafb',
    surfaceActive: '#f3f4f6',
    surfaceDisabled: '#e5e7eb',
    surfaceElevated: 'rgba(255, 255, 255, 0.98)',
    
    // Border colors
    border: '#e5e7eb',
    borderLight: '#f3f4f6',
    borderDark: '#d1d5db',
    borderFocus: '#3b82f6',
    
    // Text colors
    textPrimary: '#111827',
    textSecondary: '#6b7280',
    textTertiary: '#9ca3af',
    textDisabled: '#d1d5db',
    textInverse: '#ffffff',
    textLink: '#3b82f6',
    textLinkHover: '#2563eb',
  },
  
  typography: {
    // Font families
    fontFamilyBase: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontFamilyHeading: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontFamilyMonospace: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
    fontFamilySerif: '"Lora", Georgia, Cambria, "Times New Roman", Times, serif',
    
    // Font sizes
    fontSizeXs: '0.75rem',
    fontSizeSm: '0.875rem',
    fontSizeBase: '1rem',
    fontSizeLg: '1.125rem',
    fontSizeXl: '1.25rem',
    fontSize2xl: '1.5rem',
    fontSize3xl: '1.875rem',
    fontSize4xl: '2.25rem',
    
    // Font weights
    fontWeightLight: 300,
    fontWeightNormal: 400,
    fontWeightMedium: 500,
    fontWeightSemibold: 600,
    fontWeightBold: 700,
    
    // Line heights
    lineHeightTight: 1.25,
    lineHeightBase: 1.5,
    lineHeightRelaxed: 1.625,
    lineHeightLoose: 2,
    
    // Letter spacing
    letterSpacingTight: '-0.025em',
    letterSpacingNormal: '0',
    letterSpacingWide: '0.025em',
  },
  
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
    '4xl': '6rem',
  },
  
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    base: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    full: '9999px',
  },
  
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    soft: '0 2px 8px rgba(0, 0, 0, 0.08)',
    glow: '0 0 20px rgba(59, 130, 246, 0.3)',
    elevate: '0 10px 40px rgba(0, 0, 0, 0.15)',
  },
  
  transitions: {
    fast: '150ms ease-in-out',
    base: '250ms ease-in-out',
    slow: '350ms ease-in-out',
    bezier: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
  
  components: {
    button: {
      paddingX: '1rem',
      paddingY: '0.5rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      borderRadius: '0.5rem',
      transition: 'all 250ms ease-in-out',
      primary: {
        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        color: '#ffffff',
        borderColor: 'transparent',
        hoverBackground: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        hoverColor: '#ffffff',
        activeBackground: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
        shadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
      },
      secondary: {
        background: '#ffffff',
        color: '#374151',
        borderColor: '#e5e7eb',
        hoverBackground: '#f9fafb',
        hoverColor: '#111827',
        activeBackground: '#f3f4f6',
        shadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      },
      ghost: {
        background: 'transparent',
        color: '#6b7280',
        borderColor: 'transparent',
        hoverBackground: '#f9fafb',
        hoverColor: '#374151',
        activeBackground: '#f3f4f6',
      },
      danger: {
        background: '#ef4444',
        color: '#ffffff',
        borderColor: 'transparent',
        hoverBackground: '#dc2626',
        hoverColor: '#ffffff',
        activeBackground: '#b91c1c',
        shadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
      },
    },
    
    input: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      fontSize: '0.875rem',
      borderRadius: '0.5rem',
      borderColor: '#e5e7eb',
      background: '#ffffff',
      color: '#111827',
      placeholderColor: '#9ca3af',
      focusBorderColor: '#3b82f6',
      focusShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
      disabledBackground: '#f9fafb',
      disabledColor: '#9ca3af',
    },
    
    card: {
      padding: '1.5rem',
      borderRadius: '1rem',
      background: '#ffffff',
      borderColor: '#e5e7eb',
      shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      hoverShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    },
    
    modal: {
      overlayBackground: 'rgba(0, 0, 0, 0.5)',
      background: '#ffffff',
      borderRadius: '1rem',
      padding: '2rem',
      shadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      maxWidth: '32rem',
    },
    
    table: {
      headerBackground: '#f9fafb',
      headerColor: '#374151',
      headerFontWeight: 600,
      rowBackground: '#ffffff',
      rowHoverBackground: '#f9fafb',
      rowSelectedBackground: '#eff6ff',
      borderColor: '#e5e7eb',
      stripedBackground: '#f9fafb',
    },


    navigation: {
      background: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      linkColor: '#6b7280',
      linkHoverColor: '#374151',
      linkActiveColor: '#3b82f6',
      linkActiveBackground: '#eff6ff',
    },
    
    badge: {
      paddingX: '0.5rem',
      paddingY: '0.125rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
      defaultBackground: '#f3f4f6',
      defaultColor: '#374151',
      primaryBackground: '#dbeafe',
      primaryColor: '#1e40af',
      successBackground: '#d1fae5',
      successColor: '#065f46',
      warningBackground: '#fed7aa',
      warningColor: '#92400e',
      errorBackground: '#fee2e2',
      errorColor: '#991b1b',
    },
    
    tooltip: {
      background: '#1f2937',
      color: '#ffffff',
      fontSize: '0.75rem',
      padding: '0.375rem 0.625rem',
      borderRadius: '0.375rem',
      shadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    },
    
    dropdown: {
      background: '#ffffff',
      borderColor: '#e5e7eb',
      borderRadius: '0.5rem',
      shadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      itemPadding: '0.5rem 0.75rem',
      itemHoverBackground: '#f9fafb',
      itemHoverColor: '#111827',
      itemActiveBackground: '#f3f4f6',
      dividerColor: '#e5e7eb',
    },
    
    progress: {
      height: '0.5rem',
      background: '#e5e7eb',
      fillBackground: '#3b82f6',
      borderRadius: '9999px',
    },
    
    alert: {
      padding: '1rem',
      borderRadius: '0.75rem',
      fontSize: '0.875rem',
      info: {
        background: 'linear-gradient(to right, #eff6ff, #dbeafe)',
        color: '#1e40af',
        borderColor: '#93c5fd',
        iconColor: '#3b82f6',
      },
      success: {
        background: 'linear-gradient(to right, #f0fdf4, #dcfce7)',
        color: '#14532d',
        borderColor: '#86efac',
        iconColor: '#22c55e',
      },
      warning: {
        background: 'linear-gradient(to right, #fffbeb, #fef3c7)',
        color: '#713f12',
        borderColor: '#fde047',
        iconColor: '#f59e0b',
      },
      error: {
        background: 'linear-gradient(to right, #fef2f2, #fee2e2)',
        color: '#7f1d1d',
        borderColor: '#fca5a5',
        iconColor: '#ef4444',
      },
    },
    
    sidebar: {
      width: '20rem',
      background: '#f9fafb',
      borderColor: '#e5e7eb',
      itemPadding: '0.75rem 1rem',
      itemHoverBackground: '#f3f4f6',
      itemActiveBackground: '#e0e7ff',
      itemActiveColor: '#3730a3',
    },
    
    // Enhanced category card styling for grouped view
    categoryCard: {
      background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
      shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      hoverShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      borderRadius: '1rem',
      padding: '1.5rem',
      titleColor: '#1e293b',
      subtitleColor: '#64748b',
      tagBackground: 'rgba(59, 130, 246, 0.1)',
      tagColor: '#1e40af',
      progressBarBackground: '#e2e8f0',
      progressBarFill: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
      transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
      hoverTransform: 'translateY(-4px) scale(1.02)',
      activeTransform: 'translateY(-2px) scale(1.01)',
      avatarSize: '3rem',
      avatarGradient: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)',
      titleFontSize: '1.125rem',
      titleFontWeight: 600,
      itemBackground: 'rgba(248, 250, 252, 0.6)',
      itemHoverBackground: 'rgba(241, 245, 249, 0.8)',
      itemBorderRadius: '0.5rem',
      itemPadding: '0.75rem 1rem',
      itemTransition: 'all 150ms ease-in-out',
      progressBarHeight: '0.375rem',
      tagBorderRadius: '9999px',
      tagPadding: '0.25rem 0.75rem',
      tagFontSize: '0.75rem',
      tagFontWeight: 500,
    },

    notes: {
      fontFamily: '"Lora", Georgia, Cambria, "Times New Roman", Times, serif',
      fontSize: '1.125rem',
      lineHeight: 1.75,
      color: '#292929',
      selectionBackground: '#fff9c4',
      selectionColor: '#000000',
      titleFontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      titleFontSize: '2.25rem',
      titleFontWeight: 700,
      titleColor: '#1e293b',
      bulletColor: '#64748b',
      linkColor: '#3b82f6',
      linkHoverColor: '#1d4ed8',
      codeBackground: 'rgba(241, 245, 249, 0.8)',
      codeColor: '#1e293b',
      quoteBorderColor: '#e2e8f0',
      quoteColor: '#64748b',
    },
  },
  
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
  
  breakpoints: {
    xs: '0px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
};